import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ionasiaLogo from "../assets/ionAsia_logo_theme.jpg";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.ionekyc.com";

export default function QuickVerification() {
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [weChatId, setWeChatId] = useState("");

  const [companyQuery, setCompanyQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resultMessage, setResultMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [qrImageUrl, setQrImageUrl] = useState("");
  const [caseId, setCaseId] = useState("");

  useEffect(() => {
    if (companyQuery.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/clients/search?q=${encodeURIComponent(companyQuery)}`
        );
        const data = await res.json();
        setSuggestions(data);
      } catch (err) {
        console.error("Client search error:", err.message);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [companyQuery]);

  const handleSelectCompany = (client) => {
    setCompanyQuery(client.client_name);
    setSelectedClientId(client.client_id);
    setShowSuggestions(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setResultMessage("");
    setIsError(false);
    setQrImageUrl("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/verification/generate-qr`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, wechatId: weChatId, clientId: selectedClientId }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setQrImageUrl(data.qrImageUrl);
        setCaseId(data.caseId);
        setResultMessage("QR code generated. Please scan it with WeChat mobile application.");
        setIsError(false);
      } else {
        setResultMessage(data.message || `Error: Server returned status ${response.status}`);
        setIsError(true);
      }
    } catch (err) {
      setResultMessage(`Error: ${err.message}`);
      setIsError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = name.trim() !== "" && weChatId.trim() !== "" && selectedClientId !== null;

  return (
    <div className="auth-wrapper qv-page-wrapper">
      <div className="qv-instructions-panel">
        <h3>How to Complete Verification</h3>
        <ol>
          <li>Enter your full legal name exactly as it appears on your PRC ID.</li>
          <li>Provide your mobile number or WeChat ID.</li>
          <li>Search and select your company from the drop down list.</li>
          <li>Click "Generate QR Code" and scan it using WeChat.</li>
          <li>Once you scan the QR code in WeChat, you will be prompted to follow the official I-OnAsia account. Click Follow and wait a few seconds to receive the verification link.</li>
          <li>Press the link and start your verification.</li>
          <li>Follow the instructions provided to complete ID verification. When uploading or photographing your PRC ID, make sure the entire ID is captured with no edges cut off.</li>
        </ol>
        <p className="qv-note">
          Your data is encrypted and used solely for compliance verification purposes.
        </p>
      </div>

      <div className="qv-center-column">
        <div className="qv-logo-banner">
          <img src={ionasiaLogo} alt="I-OnAsia" />
        </div>

        <div className="auth-card" style={{ maxWidth: 480 }}>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <div
              style={{
                width: 56, height: 56, borderRadius: 10, background: "#0b2545",
                color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                fontWeight: 700, fontSize: 20, margin: "0 auto 12px",
              }}
            >
              IOA
            </div>
            <h2 style={{ margin: 0 }}> PRC ID Verification</h2>

            <div className="qv-trust-banner">
              <span className="qv-trust-icon">🔒</span>
              <span>
                Verify a mainland China ID instantly via WeChat's secure facial recognition and{" "}
                <strong>government-backed identity check</strong>.
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Full Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full legal name"
                required
              />
            </div>

            <div className="form-group">
              <label>Mobile Number/WeChat ID *</label>
              <input
                type="text"
                value={weChatId}
                onChange={(e) => setWeChatId(e.target.value)}
                placeholder="(+86-1232...) / wexx..."
                required
              />
            </div>

            <div className="form-group qv-autocomplete-wrapper">
              <label>Company *</label>
              <input
                type="text"
                value={companyQuery}
                onChange={(e) => {
                  setCompanyQuery(e.target.value);
                  setSelectedClientId(null);
                  setShowSuggestions(true);
                }}
                placeholder="Start typing your company name..."
                required
              />
              {showSuggestions && suggestions.length > 0 && (
                <ul className="qv-suggestions-list">
                  {suggestions.map((client) => (
                    <li key={client.client_id} onClick={() => handleSelectCompany(client)}>
                      {client.client_name}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {isError && <div className="form-error">{resultMessage}</div>}

            <button
              type="submit"
              className="btn-primary"
              style={{ width: "100%", marginTop: 8 }}
              disabled={!isFormValid || isSubmitting}
            >
              {isSubmitting ? "Generating..." : "Generate QR Code"}
            </button>
          </form>

          {resultMessage && !isError && (
            <div className="qv-success-msg">{resultMessage}</div>
          )}

          {qrImageUrl && (
            <div className="qv-qr-result">
              <p style={{ fontSize: 13, color: "#6b7280" }}>Case ID: {caseId}</p>
              <img src={qrImageUrl} alt="Verification QR Code" />
              <p style={{ fontSize: 13, color: "#6b7280" }}>
                Please open WeChat and scan this QR code.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="qv-instructions-panel">
        <h3>如何完成身份验证</h3>
        <ol>
          <li>请输入与您的中国身份证一致的完整法定姓名。</li>
          <li>提供您的手机号码或微信号。</li>
          <li>搜索并选择您的公司。</li>
          <li>点击"生成二维码"，然后使用微信扫描。</li>
          <li>使用微信扫描二维码后，系统会提示您关注I-OnAsia官方账号。点击"关注"并等待几秒钟以接收验证链接。</li>
          <li>点击链接，开始您的验证。</li>
          <li>按照提供的说明完成身份验证。上传或拍摄中国身份证照片时，请确保身份证完整清晰，边缘不被裁切。</li>
        </ol>
        <p className="qv-note">
          您的数据将被加密，仅用于合规验证目的。
        </p>
      </div>
    </div>
  );
}